/**
 * AddStreamReportCtrl controller
 * Add/Edit Reports
 */

import { STREAM_REPORT_REPORTING_METHOD, STREAM_TYPES, REPORT_LAYOUT_CHART_TYPES, GET_LANGUAGES, GET_COUNTRIES, REPORT_DYNAMIC_FIELDS_TITLES, REPORT_DISPLAY_MODE_TYPES, PLATFORM_TYPES } from 'app/core/constants'
import { ascBy, lowerCaseProperty } from 'app/helpers/object'
import { keyValue } from 'app/helpers/entity'

import moment from 'moment'

/**
 * AddStreamReportCtrl controller
 *
 * @class AddStreamReportCtrl
 * @memberof mwApp/reports
 * @see mwApp/reports
 *
 * @ngdoc controller
 * @name mwApp/reports.controller:AddStreamReportCtrl
 */
/* @ngInject */
export default function AddStreamReportCtrl ($scope, $location, $http, alertLiteSvc, ReportStream, StreamsReportSeed, Network, Filter, authSvc, commonData) {
  var vm = this

  /**
   * Stream reporting constants
   */
  vm.reportingMethod = STREAM_REPORT_REPORTING_METHOD
  vm.streamTypes = STREAM_TYPES
  vm.reportDisplayModeTypes = REPORT_DISPLAY_MODE_TYPES
  vm.reportLayoutChartTypes = REPORT_LAYOUT_CHART_TYPES
  vm.reportDynamicFieldsTitles = REPORT_DYNAMIC_FIELDS_TITLES

  vm.visiblePlatformTypes = PLATFORM_TYPES

  vm.reportDates = {
    default: '- Custom -',
    lastHour: 'Last Hour',
    last6Hours: 'Last 6 Hours',
    last24Hours: 'Last 24 Hours',
    today: 'Today',
    yesterday: 'Yesterday',
    last7Days: 'Last 7 Days'
  }

  /**
   * @description renaming platform names
   *
   * @param names {Array||String}
   * @returns {Array||String}
   */
  vm.getVisiblePlatformNames = function (names) {
    const findName = (name) => vm.visiblePlatformTypes.find((type) => type.name === name).visibleName

    if (Array.isArray(names)) {
      return names.map(item => findName(item))
    } else {
      return findName(names)
    }
  }

  /**
   * Listen tab event
   */
  $scope.$on('dispatch: stream_reports', function () {
    $scope.$broadcast('refreshTable')
  })

  /**
   * Report state model
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   * @type {Object}
   */
  const report = vm.report = new StreamsReportSeed()

  /**
   * Load extra data [packages, type]
   */
  ReportStream.loadExtra({
    networks: ['Network']
  }).then(
    data => {
      vm.packages = data.networks.map(keyValue())
      vm.packages.sort(ascBy(lowerCaseProperty('value')))
      vm.packages = [{ key: null, value: 'All Organizations' }].concat(vm.packages)
    }
  )

  // Generate values for fields
  report._setAbstractValues('STATUS', ['Active', 'Expired'].map((item, index) => ({ name: !index, value: item })))
  GET_COUNTRIES()
    .then((items) => items.map((item) => ({ name: item.code, value: item.name })))
    .then((countries) => {
      report._setAbstractValues('COUNTRY', countries)
      report._setAbstractValues('LOCATION_COUNTRY', countries)
    })
  GET_LANGUAGES()
    .then((items) => items.map((item) => ({ name: item, value: item })))
    .then((languages) => report._setAbstractValues('LANGUAGE', languages))

  /**
   * Enable table limit
   *
   * @type {boolean}
   */
  vm.enableTableLimit = false

  /**
   * Get filter object from report
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   * @param {String} name
   * @returns {Filter}
   */
  vm.filter = (name) =>
    report.$filtersList[name]

  // disable by flag
  vm.filter('STREAM_PLATFORMS').abstractFilterValues = angular.copy(vm.filter('STREAM_PLATFORMS').abstractFilterValues.filter((v) => {
    return !(!authSvc.hasEnabledFlag('WEB_PLATFORM_ENABLED') && v === 'WEB')
  }))

  /**
   * Get filter object from report
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   * @param {String} name - filter name
   * @param {String} key - param name
   * @param {*} value - param's value
   * @returns {Filter}
   */
  vm.changeFilter = (name, key, value) => {
    report.$filtersList[name][key] = value
  }

  /**
   * "From" datepicker settings
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.dpCfgFrom = {
    format: 'dd.MM.yyyy HH:mm',
    maxDate: new Date(),
    changeHandler: function () {
      vm.reportDate = ''
      adjustDefaultDisplayMode()
    }
  }

  /**
   * "To" datepicker settings
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.dpCfgTo = {
    format: 'dd.MM.yyyy HH:mm',
    maxDate: new Date(),
    minDate: new Date(),
    changeHandler: function () {
      vm.reportDate = ''
      adjustDefaultDisplayMode()
    }
  }

  /**
   * Handle open "from" datepicker
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.openFrom = function () {
    vm.dpCfgFrom.opened = true
  }

  /**
   * Handle open "to" datepicker
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.openTo = function () {
    vm.dpCfgTo.opened = true
  }

  $scope.$watch('vm.reportDate', (value) => {
    vm.selectDateInterval(value)
  })

  // Selected interval by Report Date
  // TODO: move to directive
  vm.reportDate = 'last24Hours'
  vm.selectDateInterval = (type) => {
    let from = new Date()
    let to = moment().add(1, 'day').toDate()

    switch (type) {
      case 'lastHour':
        from = moment().subtract(1, 'hour').toDate()
        to = moment().toDate()
        break
      case 'last6Hours':
        from = moment().subtract(6, 'hour').toDate()
        to = moment().toDate()
        break
      case 'last24Hours':
        from = moment().subtract(24, 'hour').toDate()
        to = moment().toDate()
        break
      case 'today':
        from = moment().toDate().setHours(0, 0)
        to = moment().toDate().setHours(23, 59)
        break
      case 'yesterday':
        from = moment().subtract(1, 'day').toDate().setHours(0, 0)
        to = moment().subtract(1, 'day').toDate().setHours(23, 59)
        break
      case 'last7Days':
        from = moment().subtract(7, 'day').toDate().setHours(0, 0)
        to = moment().toDate().setHours(23, 59)
        break
      /*
      case 'last30Days':
        from = moment().subtract(30, 'day').toDate().setHours(0, 0)
        to = moment().toDate().setHours(23, 59)
        break
      case 'last3Months':
        from = moment().subtract(3, 'month').toDate().setHours(0, 0)
        to = moment().toDate().setHours(23, 59)
        break
      case 'lastYear':
        from = moment().subtract(1, 'year').toDate().setHours(0, 0)
        to = moment().toDate().setHours(23, 59)
        break
      */
    }

    if (type) {
      vm.report.$filtersList.STREAM_DATE_FROM_TO.$fromDateTime = from
      vm.report.$filtersList.STREAM_DATE_FROM_TO.$toDateTime = to
    }

    adjustDefaultDisplayMode()
  }

  function adjustDefaultDisplayMode () {
    const from = vm.filter('STREAM_DATE_FROM_TO').$fromDateTime

    // limit "To" date to maximum 7 days from "From" date
    if (from != null) {
      let max = moment(from).add(7, 'day').subtract(1, 'minute')
      if (max.isAfter()) {
        max = moment()
      }

      vm.dpCfgTo.minDate = from
      vm.dpCfgTo.maxDate = max.toDate()

      // adjust "To" date, if out of limits
      const _to = vm.filter('STREAM_DATE_FROM_TO').$toDateTime
      if (_to != null) {
        if (moment(_to).isAfter(max)) {
          vm.filter('STREAM_DATE_FROM_TO').$toDateTime = vm.dpCfgTo.maxDate
        }
        if (moment(_to).isBefore(from)) {
          vm.filter('STREAM_DATE_FROM_TO').$toDateTime = from
        }
      }
    }

    const to = vm.filter('STREAM_DATE_FROM_TO').$toDateTime

    if (from != null && to != null) {
      let setList = ['HOUR', 'DAY']
      let set = 'HOUR'
      const diff = moment(to).diff(from, 'days', true)

      if (diff > 3) {
        set = 'DAY'
        setList = ['DAY', 'MONTH']
      }
      if (diff > 30 * 3) {
        set = 'MONTH'
        setList = ['MONTH']
      }

      vm.filter('REPORT_DISPLAY_MODE').displayMode = set
      vm.filter('REPORT_DISPLAY_MODE').abstractFilterValues = setList
    }
  }

  /**
   * Generate report
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.generateReport = function () {
    vm.setDisable()
    vm.report
      ._save(
        vm.checkNetworks,
        'api/reports/streams'
      )
      .then(function (response) {
        $location.hash('generated_reports')

        commonData.data.update('lastReport', response.data.id)

        vm.location.reload()
      })
      .finally(vm.unsetDisable)
  }

  vm.checkNetworks = function (report) {
    const networksIndex = report.filters.findIndex(filter => filter.reportElementType === 'STREAM_NETWORKS')
    const filterNetworks = report.filters[networksIndex].networkIds.filter((network) => network === null).length
    if (networksIndex > 0 && filterNetworks) {
      report.filters[networksIndex].networkIds = []
    }
  }

  /**
   * Save reports
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.submit = function () {
    vm.setDisable()
    vm.report
      ._save(
        vm.checkNetworks,
        'api/reporttemplates/streams'
      )
      .then(function (response) {
        $location.hash('reports_list')

        commonData.data.update('lastTemplate', response.data.id)

        vm.location.reload()
      })
      .finally(vm.unsetDisable)
  }

  /**
   * Handle table limit
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.changeTableLimit = () => {
    vm.filter('REPORT_LAYOUT_TABLE_LIMIT').tableLimit = vm.enableTableLimit ? -1 : 10
  }

  /**
   * Filter selected streams on stream type change
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.changeStreamType = () => {
    if (vm.filter('STREAM_TYPE').streamType != null) {
      const toRemove = []
      vm.streams = vm.streams.filter((stream) => {
        const stay = stream.streamType === vm.filter('STREAM_TYPE').streamType
        if (!stay) {
          toRemove.push(stream.streamId)
        }
        return stay
      })
      vm.filter('STREAM_CHANNELS').channelIds = vm.filter('STREAM_CHANNELS').channelIds.filter((id) => toRemove.indexOf(id) === -1)
    }
  }

  /**
   * Filter selected streams on networks change
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.changeNetworks = () => {
    if (vm.filter('STREAM_NETWORKS').networkIds !== null && vm.filter('STREAM_NETWORKS').networkIds.indexOf(null) !== -1) {
      vm.filter('STREAM_NETWORKS').networkIds = vm.filter('STREAM_NETWORKS').networkIds.filter(n => n === null)
    }
    if (vm.filter('STREAM_NETWORKS').networkIds != null && vm.filter('STREAM_NETWORKS').networkIds.length && vm.filter('STREAM_NETWORKS').networkIds.indexOf(null) === -1) {
      const toRemove = []
      vm.streams = vm.streams.filter((stream) => {
        const stay = stream.networkIds.some((id) => vm.filter('STREAM_NETWORKS').networkIds.indexOf(id) !== -1)
        if (!stay) {
          toRemove.push(stream.streamId)
        }
        return stay
      })
      vm.filter('STREAM_CHANNELS').channelIds = vm.filter('STREAM_CHANNELS').channelIds.filter((id) => toRemove.indexOf(id) === -1)
    }
  }

  /**
   * Handle search subscribers
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   * @param search
   */
  vm.subscribers = []
  vm.searchSubscribers = (search) => {
    if (search) {
      vm.report._searchSubscriber(search)
        .data()
        .then((data) => {
          vm.subscribers = data.subscribers
        })
    }
  }

  /**
   * Handle search streams
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   * @param search
   */
  vm.streams = []
  vm.searchStreams = (search) => {
    if (search) {
      vm.report._searchStreams(search, vm.filter('STREAM_TYPE').streamType, vm.filter('STREAM_NETWORKS').networkIds)
        .data()
        .then((data) => {
          vm.streams = data.streams
        })
    }
  }

  /**
   * Handle search devices
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   * @param search
   */
  vm.devices = []
  vm.searchDevices = (search) => {
    if (search) {
      vm.report._searchDevices(search)
        .data()
        .then((data) => {
          vm.devices = StreamsReportSeed.cast(data.devices, ['Device'])
        })
    }
  }

  /**
   * Add item to subscriber filters list
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   * @param item
   */
  vm.addSubscriberFilter = (item) => {
    vm.report.$subscriberFilters.push({ ...item })
  }

  /**
   * Delete item from subscriber filters list
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   * @param key
   */
  vm.deleteSubscriberFilter = (key) => {
    vm.report.$subscriberFilters.splice(key, 1)
  }

  /**
   * Add item to device filters list
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   * @param item
   */
  vm.addDeviceFilter = (item) => {
    vm.report.$deviceFilters.push({ ...item })
  }

  /**
   * Delete item from device filters list
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   * @param key
   */
  vm.deleteDeviceFilter = (key) => {
    vm.report.$deviceFilters.splice(key, 1)
  }

  /**
   * Remove callback for ui-select
   */
  vm.removedDevices = () => {
    vm.devices = []
  }

  /**
   * Remove callback for ui-select
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.removedSubscribers = () => {
    vm.subscribers = []
  }

  /**
   * Remove callback for ui-select
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.removedStreams = () => {
    vm.streams = []
  }

  /**
   *
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.changeSubscriberFields = () => {
    if (vm.filter('STREAM_SUBSCRIBERS').subscriberIds) {
      vm.report.$subscriberFilters = []
    }
  }

  /**
   *
   *
   * @memberof mwApp/reports.controller:AddStreamReportCtrl
   */
  vm.changeDeviceFields = () => {
    if (vm.filter('STREAM_DEVICES').deviceIds) {
      vm.report.$deviceFilters = []
    }
  }

  /**
   * Search value in array
   *
   * @param value
   * @param array
   * @returns {boolean}
   */
  vm.inArray = (value, array) => {
    return array.indexOf(value) !== -1
  }
}
